import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, Globe, Calendar, MapPin } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов конвертера
import ConverterForm from './TimeZoneConverterPage/components/ConverterForm';
import WorldClockGrid from './TimeZoneConverterPage/components/WorldClockGrid';
import MeetingPlanner from './TimeZoneConverterPage/components/MeetingPlanner';
import TimeDifferenceCalculator from './TimeZoneConverterPage/components/TimeDifferenceCalculator';
import PopularTimeZones from './TimeZoneConverterPage/components/PopularTimeZones';
import TimeZoneMap from './TimeZoneConverterPage/components/TimeZoneMap';
import SavedTimeZonesSidebar from './TimeZoneConverterPage/components/SavedTimeZonesSidebar';
import QuickSelectSidebar from './TimeZoneConverterPage/components/QuickSelectSidebar';
import TipsSidebar from './TimeZoneConverterPage/components/TipsSidebar';
import InfoContent from './TimeZoneConverterPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  sourceCity: 'Moscow',
  sourceTimezone: 'Europe/Moscow',
  targetCity: 'New_York',
  targetTimezone: 'America/New_York',
  date: new Date().toISOString().split('T')[0],
  time: new Date().toTimeString().slice(0, 5),
  includeDate: true,
  showSeconds: false,
  view: 'converter', // 'converter', 'worldclock', 'meeting', 'difference'
};

// Города России и популярные города мира с часовыми поясами
export const russianCities = [
  { id: 'Moscow', name: 'Москва', timezone: 'Europe/Moscow', utcOffset: '+3:00', coordinates: { lat: 55.7558, lng: 37.6173 } },
  { id: 'Saint_Petersburg', name: 'Санкт-Петербург', timezone: 'Europe/Moscow', utcOffset: '+3:00', coordinates: { lat: 59.9343, lng: 30.3351 } },
  { id: 'Kaliningrad', name: 'Калининград', timezone: 'Europe/Kaliningrad', utcOffset: '+2:00', coordinates: { lat: 54.7065, lng: 20.5109 } },
  { id: 'Murmansk', name: 'Мурманск', timezone: 'Europe/Moscow', utcOffset: '+3:00', coordinates: { lat: 68.9585, lng: 33.0827 } },
  { id: 'Kazan', name: 'Казань', timezone: 'Europe/Moscow', utcOffset: '+3:00', coordinates: { lat: 55.7887, lng: 49.1221 } },
  { id: 'Yekaterinburg', name: 'Екатеринбург', timezone: 'Asia/Yekaterinburg', utcOffset: '+5:00', coordinates: { lat: 56.8389, lng: 60.6057 } },
  { id: 'Novosibirsk', name: 'Новосибирск', timezone: 'Asia/Novosibirsk', utcOffset: '+7:00', coordinates: { lat: 55.0084, lng: 82.9357 } },
  { id: 'Krasnoyarsk', name: 'Красноярск', timezone: 'Asia/Krasnoyarsk', utcOffset: '+7:00', coordinates: { lat: 56.0184, lng: 92.8672 } },
  { id: 'Irkutsk', name: 'Иркутск', timezone: 'Asia/Irkutsk', utcOffset: '+8:00', coordinates: { lat: 52.2978, lng: 104.2964 } },
  { id: 'Yakutsk', name: 'Якутск', timezone: 'Asia/Yakutsk', utcOffset: '+9:00', coordinates: { lat: 62.0355, lng: 129.6755 } },
  { id: 'Vladivostok', name: 'Владивосток', timezone: 'Asia/Vladivostok', utcOffset: '+10:00', coordinates: { lat: 43.1198, lng: 131.8869 } },
  { id: 'Kamchatka', name: 'Петропавловск-Камчатский', timezone: 'Asia/Kamchatka', utcOffset: '+12:00', coordinates: { lat: 53.0242, lng: 158.6480 } },
];

export const worldCities = [
  { id: 'New_York', name: 'Нью-Йорк', timezone: 'America/New_York', utcOffset: '-5:00/-4:00', coordinates: { lat: 40.7128, lng: -74.0060 } },
  { id: 'London', name: 'Лондон', timezone: 'Europe/London', utcOffset: '+0:00/+1:00', coordinates: { lat: 51.5074, lng: -0.1278 } },
  { id: 'Berlin', name: 'Берлин', timezone: 'Europe/Berlin', utcOffset: '+1:00/+2:00', coordinates: { lat: 52.5200, lng: 13.4050 } },
  { id: 'Paris', name: 'Париж', timezone: 'Europe/Paris', utcOffset: '+1:00/+2:00', coordinates: { lat: 48.8566, lng: 2.3522 } },
  { id: 'Dubai', name: 'Дубай', timezone: 'Asia/Dubai', utcOffset: '+4:00', coordinates: { lat: 25.2048, lng: 55.2708 } },
  { id: 'Delhi', name: 'Дели', timezone: 'Asia/Kolkata', utcOffset: '+5:30', coordinates: { lat: 28.6139, lng: 77.2090 } },
  { id: 'Beijing', name: 'Пекин', timezone: 'Asia/Shanghai', utcOffset: '+8:00', coordinates: { lat: 39.9042, lng: 116.4074 } },
  { id: 'Tokyo', name: 'Токио', timezone: 'Asia/Tokyo', utcOffset: '+9:00', coordinates: { lat: 35.6762, lng: 139.6503 } },
  { id: 'Sydney', name: 'Сидней', timezone: 'Australia/Sydney', utcOffset: '+10:00/+11:00', coordinates: { lat: -33.8688, lng: 151.2093 } },
  { id: 'Los_Angeles', name: 'Лос-Анджелес', timezone: 'America/Los_Angeles', utcOffset: '-8:00/-7:00', coordinates: { lat: 34.0522, lng: -118.2437 } },
];

// Получение всех городов
export const getAllCities = () => [...russianCities, ...worldCities];

/**
 * TimeZoneConverterPage Component - Конвертер часовых поясов с функциями мирового времени, 
 * планировщика встреч и расчета разницы во времени.
 */
const TimeZoneConverterPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('timezone-converter'), []);
  const pageTitle = tool?.metaTitle || 'Конвертер часовых поясов | Gensio';
  const metaDescription = tool?.metaDescription || 'Конвертер часовых поясов онлайн: проверка точного времени в разных городах мира, расчет разницы во времени, планировщик международных встреч с учетом часовых поясов России и других стран.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [convertedTime, setConvertedTime] = useState(null);
  const [timeDifference, setTimeDifference] = useState(null);
  const [worldClocks, setWorldClocks] = useState([]);
  const [selectedTimeZones, setSelectedTimeZones] = useState([]);
  const [savedTimeZones, setSavedTimeZones] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных часовых поясов из localStorage ---
  useEffect(() => {
    const savedData = localStorage.getItem('savedTimeZones');
    if (savedData) {
      try {
        setSavedTimeZones(JSON.parse(savedData));
      } catch (e) {
        console.error("Error loading saved time zones:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'world-clock':
          newState.view = 'worldclock';
          break;
        case 'meeting-planner':
          newState.view = 'meeting';
          break;
        case 'time-difference':
          newState.view = 'difference';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setConvertedTime(null);
    setTimeDifference(null);
    setWorldClocks([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение набора часовых поясов
  const saveTimeZone = (name) => {
    if (!convertedTime && formData.view !== 'worldclock') return;

    const newSavedTimeZone = {
      id: Date.now().toString(),
      name: name || `${formData.sourceCity} → ${formData.targetCity}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      sourceCity: getAllCities().find(city => city.id === formData.sourceCity),
      targetCity: getAllCities().find(city => city.id === formData.targetCity),
    };

    const updatedSavedTimeZones = [...savedTimeZones, newSavedTimeZone];
    setSavedTimeZones(updatedSavedTimeZones);
    localStorage.setItem('savedTimeZones', JSON.stringify(updatedSavedTimeZones));

    return newSavedTimeZone;
  };

  // Удаление сохраненного часового пояса
  const deleteSavedTimeZone = (id) => {
    const updatedSavedTimeZones = savedTimeZones.filter(tz => tz.id !== id);
    setSavedTimeZones(updatedSavedTimeZones);
    localStorage.setItem('savedTimeZones', JSON.stringify(updatedSavedTimeZones));
  };

  // Загрузка сохраненного часового пояса
  const loadSavedTimeZone = (id) => {
    const tzToLoad = savedTimeZones.find(tz => tz.id === id);
    if (tzToLoad) {
      setFormData(tzToLoad.formData);
      
      // Выполняем соответствующий расчет
      setTimeout(() => {
        document.querySelector('button[type="submit"]')?.click();
      }, 100);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Clock className="w-8 h-8 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                {tool?.name || 'Конвертер часовых поясов'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма конвертера --- */}
            <ConverterForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              setConvertedTime={setConvertedTime}
              setTimeDifference={setTimeDifference}
              setWorldClocks={setWorldClocks}
              russianCities={russianCities}
              worldCities={worldCities}
            />

            {/* --- Результаты конвертации --- */}
            {formData.view === 'converter' && convertedTime && !isCalculating && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-purple-500" />
                    Результат конвертации времени
                  </h2>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-800">
                    <h3 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">
                      {getAllCities().find(city => city.id === formData.sourceCity)?.name || formData.sourceCity}
                    </h3>
                    <p className="text-3xl font-bold text-purple-800 dark:text-purple-200">{convertedTime.sourceTime}</p>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                      {convertedTime.sourceDate} • UTC{convertedTime.sourceOffset}
                    </p>
                  </div>
                  
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
                    <h3 className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-1">
                      {getAllCities().find(city => city.id === formData.targetCity)?.name || formData.targetCity}
                    </h3>
                    <p className="text-3xl font-bold text-indigo-800 dark:text-indigo-200">{convertedTime.targetTime}</p>
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                      {convertedTime.targetDate} • UTC{convertedTime.targetOffset}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg text-sm text-gray-600 dark:text-gray-400">
                  <p className="flex items-center">
                    <MapPin className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
                    Разница во времени: <span className="font-semibold ml-1 text-gray-800 dark:text-gray-200">{convertedTime.difference}</span>
                  </p>
                </div>
              </div>
            )}

            {/* --- Мировые часы --- */}
            {formData.view === 'worldclock' && !isCalculating && (
              <WorldClockGrid 
                worldClocks={worldClocks} 
                selectedTimeZones={selectedTimeZones}
                setSelectedTimeZones={setSelectedTimeZones}
                saveTimeZone={saveTimeZone}
              />
            )}

            {/* --- Планировщик встреч --- */}
            {formData.view === 'meeting' && !isCalculating && (
              <MeetingPlanner 
                formData={formData}
                setFormData={setFormData}
                russianCities={russianCities}
                worldCities={worldCities}
              />
            )}

            {/* --- Калькулятор разницы во времени --- */}
            {formData.view === 'difference' && timeDifference && !isCalculating && (
              <TimeDifferenceCalculator 
                formData={formData}
                timeDifference={timeDifference}
                russianCities={russianCities}
                worldCities={worldCities}
              />
            )}

            {/* --- Популярные часовые пояса России --- */}
            <PopularTimeZones 
              russianCities={russianCities}
              setFormData={setFormData}
            />

            {/* --- Карта часовых поясов --- */}
            <TimeZoneMap 
              selectedCity={getAllCities().find(city => 
                (formData.view === 'converter' || formData.view === 'difference') 
                  ? city.id === formData.targetCity 
                  : city.id === formData.sourceCity
              )}
            />

            {/* --- Информационный контент о часовых поясах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрого выбора --- */}
            <QuickSelectSidebar 
              formData={formData}
              setFormData={setFormData}
              russianCities={russianCities}
              worldCities={worldCities}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сохраненных часовых поясов --- */}
            <SavedTimeZonesSidebar 
              savedTimeZones={savedTimeZones}
              deleteSavedTimeZone={deleteSavedTimeZone}
              loadSavedTimeZone={loadSavedTimeZone}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Clock styling */
        .clock-display {
          @apply text-2xl sm:text-3xl md:text-4xl font-bold text-center;
          font-variant-numeric: tabular-nums;
        }
        .clock-colon {
          @apply opacity-80 inline-block;
          animation: blink 1s infinite;
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default TimeZoneConverterPage;